/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  InputAdornment,
  Switch,
  Typography,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PaletteIcon from "@material-ui/icons/Palette";
import SearchIcon from "@material-ui/icons/Search";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import BrightnessAutoIcon from "@material-ui/icons/BrightnessAuto";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import A_TextField from "../../../../Atoms/Inputs/TextFields/A_TextField";
import ThemePreviewMock from "./ThemePreviewMock";
import {
  STRINGS,
  MODIFIER_KEY,
  THEME_OPTIONS,
  MODES,
  applyTheme,
  getStoredTheme,
  getStoredMode,
  getNextMode,
  cycleMode,
  getResolvedMode,
  applySidebarVibrant,
  getStoredSidebarVibrant,
} from "./M_ThemeSelector.helper";
import "./M_ThemeSelector.css";

const MODE_ICONS = {
  [MODES.LIGHT]: Brightness7Icon,
  [MODES.DARK]: Brightness4Icon,
  [MODES.SYSTEM]: BrightnessAutoIcon,
};

const M_ThemeSelector = (props) => {
  const { onThemeChange } = props;
  const [activeTheme, setActiveTheme] = useState(getStoredTheme());
  const [mode, setMode] = useState(getStoredMode());
  // Tracked as state (not derived from `mode`) so an OS-level flip while
  // mode is "system" still re-renders the preview mocks
  const [isDarkMode, setIsDarkMode] = useState(
    getResolvedMode() === MODES.DARK
  );
  const [query, setQuery] = useState("");
  const [spin, setSpin] = useState(0);
  const [sidebarVibrant, setSidebarVibrant] = useState(
    getStoredSidebarVibrant()
  );
  const ModeIcon = MODE_ICONS[mode] || Brightness4Icon;
  const nextModeLabel = STRINGS.MODE_LABELS[getNextMode(mode)];

  useEffect(() => {
    setActiveTheme(getStoredTheme());
    setMode(getStoredMode());
    setIsDarkMode(getResolvedMode() === MODES.DARK);
    setSidebarVibrant(getStoredSidebarVibrant());

    const handleGlobalThemeChange = (event) => {
      const { theme, mode: nextMode } = event.detail || {};
      if (theme) {
        setActiveTheme(theme);
      }
      if (nextMode) {
        setMode(nextMode);
        // Spin the toggle icon for shortcut-driven changes too, not just clicks
        setSpin((value) => value + 1);
      }
      setIsDarkMode(getResolvedMode() === MODES.DARK);
      setSidebarVibrant(getStoredSidebarVibrant());
    };

    window.addEventListener("themeChange", handleGlobalThemeChange);
    return () => window.removeEventListener("themeChange", handleGlobalThemeChange);
  }, []);

  const handleCycleMode = (event) => {
    setSpin((value) => value + 1);
    setMode(cycleMode({ x: event.clientX, y: event.clientY }));
  };

  const handleVibrantToggle = (event) => {
    const next = event.target.checked;
    setSidebarVibrant(next);
    applySidebarVibrant(next);
  };

  const handleSelect = (themeId, event) => {
    if (!themeId || themeId === activeTheme) {
      return;
    }
    // Keyboard events carry no clientX/Y — fall back to the default wipe origin
    const coords =
      event && typeof event.clientX === "number"
        ? { x: event.clientX, y: event.clientY }
        : null;
    setActiveTheme(themeId);
    applyTheme(themeId, coords);
    if (typeof onThemeChange === "function") {
      onThemeChange(themeId);
    }
  };

  const { matchCount, themes } = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    const matches = trimmed
      ? THEME_OPTIONS.filter(
          (theme) =>
            theme.label.toLowerCase().includes(trimmed) ||
            theme.value.toLowerCase().includes(trimmed) ||
            (theme.group || "").toLowerCase().includes(trimmed)
        )
      : THEME_OPTIONS;
    return { matchCount: matches.length, themes: matches };
  }, [query]);

  const gridRef = useRef(null);

  // The cards are the only [role="radio"] nodes in the grid, so their DOM
  // order lines up with `themes`.
  const focusCard = (index) => {
    const nodes = gridRef.current
      ? gridRef.current.querySelectorAll('[role="radio"]')
      : [];
    const node = nodes[index];
    if (node && typeof node.focus === "function") {
      node.focus();
    }
  };

  // Selects the theme `step` places away in the visible (filtered) list,
  // wrapping at both ends. Shared by the arrow keys and the Ctrl/Cmd shortcuts.
  const shiftTheme = (step) => {
    if (themes.length < 2) {
      return;
    }
    const current = themes.findIndex((theme) => theme.value === activeTheme);
    // With the active theme filtered out there is nothing to step from, so
    // enter the list from whichever end the user is heading towards.
    const nextIndex =
      current === -1
        ? step > 0
          ? 0
          : themes.length - 1
        : (current + step + themes.length) % themes.length;
    handleSelect(themes[nextIndex].value);
    focusCard(nextIndex);
  };

  // Ctrl/Cmd + arrows and Ctrl/Cmd + M are handled globally by
  // initThemeShortcuts() (wired up in src/index.js) so they keep working with
  // this panel closed; the "themeChange" listener above re-syncs this UI. All
  // that is left here is keeping the focus ring on the selected card -- and
  // only while focus is already inside the grid, so a shortcut pressed
  // elsewhere never yanks focus out of whatever the user is doing.
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid || !grid.contains(document.activeElement)) {
      return;
    }
    const selected = grid.querySelector('[role="radio"][aria-checked="true"]');
    if (selected && selected !== document.activeElement) {
      selected.focus();
    }
  }, [activeTheme]);

  // Roving tabindex: a radiogroup is a single tab stop and the arrow keys move
  // within it, so only the selected card stays tabbable.
  const activeIndex = themes.findIndex((theme) => theme.value === activeTheme);
  const tabStopIndex = activeIndex === -1 ? 0 : activeIndex;

  return (
    <div className="m-theme-selector">
      <div className="m-theme-selector__hero">
        <div className="m-theme-selector__hero-copy">
          <Typography className="m-theme-selector__hero-title">
            {STRINGS.HERO_TITLE}
          </Typography>
          <Typography className="m-theme-selector__hero-subtitle">
            {STRINGS.HERO_SUBTITLE}
          </Typography>
        </div>
        <button
          type="button"
          className="m-theme-selector__mode-toggle"
          onClick={handleCycleMode}
          aria-label={STRINGS.modeSwitchAria(nextModeLabel)}
          title={STRINGS.modeSwitchAria(nextModeLabel)}
        >
          <ModeIcon />
          <span>{STRINGS.MODE_LABELS[mode] || STRINGS.MODE_LABELS[MODES.LIGHT]}</span>
          <AutorenewIcon
            key={spin}
            className={
              "m-theme-selector__mode-cycle" +
              (spin > 0 ? " m-theme-selector__mode-cycle--spin" : "")
            }
          />
        </button>
      </div>

      <div className="m-theme-selector__option">
        <div className="m-theme-selector__option-copy">
          <Typography className="m-theme-selector__option-title">
            Vibrant sidebar
          </Typography>
          <Typography className="m-theme-selector__option-desc">
            Accent-tinted gradient treatment for the side menu. Turn off for the classic flat look.
          </Typography>
        </div>
        <Switch
          color="primary"
          checked={sidebarVibrant}
          onChange={handleVibrantToggle}
          inputProps={{ "aria-label": "Toggle vibrant sidebar" }}
        />
      </div>

      <div className="m-theme-selector__toolbar">
        <div className="m-theme-selector__search">
          <A_TextField
            variant="outlined"
            label=""
            placeholder={STRINGS.SEARCH_PLACEHOLDER}
            defaultValue={query}
            inputType="value"
            fullWidth
            onChange={(event) => setQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div className="m-theme-selector__shortcuts">
          <span className="m-theme-selector__shortcut">
            <span className="m-theme-selector__kbd">{MODIFIER_KEY}</span>
            <span className="m-theme-selector__kbd">{STRINGS.KEYS.PREV}</span>
            <span className="m-theme-selector__kbd">{STRINGS.KEYS.NEXT}</span>
            {STRINGS.SHORTCUTS.SWITCH_THEME}
          </span>
          <span className="m-theme-selector__shortcut">
            <span className="m-theme-selector__kbd">{MODIFIER_KEY}</span>
            <span className="m-theme-selector__kbd">{STRINGS.KEYS.MODE}</span>
            {STRINGS.SHORTCUTS.TOGGLE_MODE}
          </span>
        </div>
      </div>

      {matchCount === 0 ? (
        <div className="m-theme-selector__empty">
          <Typography>{STRINGS.noMatchMessage(query)}</Typography>
        </div>
      ) : (
        <div
          role="radiogroup"
          aria-label={STRINGS.RADIOGROUP_LABEL}
          ref={gridRef}
        >
          <Grid container spacing={2} className="m-theme-selector__grid">
            {themes.map((theme, index) => {
              const isActive = activeTheme === theme.value;
              const cardClassName =
                "m-theme-selector__card" +
                (isActive ? " m-theme-selector__card--active" : "");

              return (
                <Grid item xs={12} sm={6} md={4} key={theme.value}>
                  <Box
                    className={cardClassName}
                    onClick={(event) => handleSelect(theme.value, event)}
                    role="radio"
                    tabIndex={index === tabStopIndex ? 0 : -1}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleSelect(theme.value, event);
                        return;
                      }
                      // Ctrl/Cmd combos belong to the window handler above
                      if (event.ctrlKey || event.metaKey || event.altKey) {
                        return;
                      }
                      // Standard radiogroup navigation. The grid reflows across
                      // breakpoints, so up/down step the same way as left/right.
                      const step =
                        event.key === "ArrowRight" || event.key === "ArrowDown"
                          ? 1
                          : event.key === "ArrowLeft" || event.key === "ArrowUp"
                          ? -1
                          : 0;
                      if (step !== 0) {
                        event.preventDefault();
                        shiftTheme(step);
                      }
                    }}
                    aria-checked={isActive}
                    aria-label={theme.label}
                  >
                    <ThemePreviewMock
                      themeId={theme.value}
                      isDark={isDarkMode}
                    />

                    <div className="m-theme-selector__title-row">
                      <div className="m-theme-selector__title-block">
                        <Typography className="m-theme-selector__title">
                          {theme.label}
                        </Typography>
                        <Typography className="m-theme-selector__meta">
                          {theme.value}
                        </Typography>
                      </div>
                      {isActive ? (
                        <div className="m-theme-selector__active-pill">
                          <CheckCircleIcon
                            className="m-theme-selector__check"
                            fontSize="small"
                          />
                          <span>{STRINGS.ACTIVE_LABEL}</span>
                        </div>
                      ) : null}
                    </div>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </div>
      )}
    </div>
  );
};

M_ThemeSelector.propTypes = {
  onThemeChange: PropTypes.func,
};

export default M_ThemeSelector;
